<template>
  <div>
    <div v-if="!this.$route.params.id">Select a user to edit</div>
    <h2 v-else-if="loading">Loading...</h2>
    <form v-else v-on:submit.prevent="save">
      <div>
        <h2>{{ selected.fullName }}</h2>
        <h2>{{ selected.email }} <span v-if="pixel.paused">(Paused)</span></h2>
        <div :class="$style.section">
          <label>Account Active</label>
          <input type="checkbox" v-model="selected.active" />
        </div>
        <div :class="$style.section">
          <label>Pixel Active</label>
          <input type="checkbox" v-model="pixel.active" />
        </div>
        <div :class="$style.section">
          <label>Debug logging</label>
          <div>
            <select v-model="pixel.debug">
              <option value="">Off</option>
              <option value="support">Clean</option>
              <option value="admin">Full</option>
            </select>
          </div>
          <br/>
        </div>
        <div :class="$style.section">
          <label>Remove Branding</label>
          <input type="checkbox" v-model="pixel.rb" />
        </div>
        <div :class="$style.section">
          <label>WatchDOM</label>
          <table v-if="campaigns.length > 0">
            <tr v-for="(campaign, cid) in campaigns">
              <td> - <code>{{campaign.name}}</code></td>
              <td><input type="checkbox" v-model="campaign.watchDOM" /></td>
            </tr>
          </table>
          <div :class="$style.note" v-else>-No campaigns created-</div>
        </div>
        <div :class="$style.section">
          <label>SPA Support</label>
          <input type="checkbox" v-model="pixel.spaCompatibility" />
        </div>
        <div :class="$style.section">
          <label>Stripe Customer ID</label>
          <ui-input v-model="selected.customer_id" />
          <label>Stripe Subscription ID</label>
          <ui-input v-model="selected.subscriptionId" />
        </div>
        <div :class="$style.section">
          <label>Current Plan</label>
          <div>
            <select v-model="selected.planId">
              <option v-for="plan in allPlans" :value="plan.id">{{plan.name}} (${{plan.amount / 100}})</option>
            </select>
            <span v-show="planChanged" :class="$style.warn">*User's plan will change on save</span>
          </div>
          <div v-show="planChanged">
            Retain the user's trial <input type="checkbox" v-model="retainTrial" />
            <div v-show="planUpgrade">
              Charge immediately <input type="checkbox" v-model="chargeImmediately" />
            </div>
          </div>
        </div>
        <div :class="$style.section">
          <label>Disable Auto Upgrade</label>
          <input type="checkbox" v-model="selected.disableAutoUpgrade" />
        </div>
        <div :class="$style.section">
          <label>Visitor Override</label>
          <div>
            <select v-model="selected.visitorLimitModifierType" v-on:change="updateModifiers">
              <option></option>
              <option value="limit">limit</option>
              <option value="add">add</option>
              <option value="multiply">multiply</option>
            </select>
          </div>
          <div v-if="selected.visitorLimitModifierType">
            <div>
              <input type="number" step=".01" v-model="visitorLimitModifier" />
            </div>
            <div>
              Reset on plan change <input type="checkbox" v-model="selected.resetOnPlanChange" />
            </div>
          </div>
        </div>
        <div :class="$style.section">
          <label>Onboarding State</label>
          <div>
            <select v-model="onboardingState">
              <option value="plans">Choose Plan</option>
              <option value="payment">Payment Info</option>
              <option value="complete">Complete</option>
            </select>
          </div>
          <br/>
        </div>
        <div :class="$style.section">
          <label>Promos</label>
          <ul>
            <li v-for="(promo, name) in selected.promos">{{name}}</li>
          </ul>
          <div>
            <select v-model="newPromo">
              <option v-for="i in allPromos" :value="i">{{i}}</option>
            </select>
            <button v-if="newPromo" v-on:click="addPromo()" type="button">Add</button>
          </div>
        </div>
      </div>
      <hr/>
      <div :class="$style.section">
        <button class="btn" :disabled="saving" type="submit">Save</button>
        <button class="btn" :disabled="saving" v-on:click="togglePause" type="button">{{pauseAction}}</button>
        <button class="btn" :disabled="saving" v-on:click="resetTraffic()" type="button">Reset Traffic</button>
      </div>
      <div v-if="saving">Saving...</div>
    </form>
  </div>
</template>

<script>
import _find from 'lodash/find'
import _remove from 'lodash/remove'
import q from 'q'
import database from '../../../database'
import uiInput from '../../lib/Form/Input'
import account from '../../../api/account'
import campaign from '../../../api/campaign'
import billing from '../../../api/billing'
import edit from './edit'
import Vue from 'vue'

export default {
  name: 'edit',
  components: {
    uiInput
  },
  data () {
    return {
      saving: false,
      selected: {},
      pixel: {},
      campaigns: {},
      onboardingState: null,
      loading: false,
      allPlans: [],
      allPromos: [],
      newPromo: null,
      addedPromos: [],
      initialPlan: '',
      retainTrial: true,
      chargeImmediately: true
    }
  },
  methods: {
    save () {
      if (this.selected.id) {
        this.saving = true
        var updateObj = edit.createUpdateObject(edit.ACCOUNT_FIELDS, this.selected)
        var promiseList = []
        promiseList.push(database.update(['accounts', this.selected.id], updateObj))

        // update plan
        if (this.selected.subscriptionId && this.planChanged) {
          this.initialPlan = this.selected.planId
          promiseList.push(billing.changePlan(this.selected.subscriptionId, this.selected.planId, this.chargeImmediately, this.retainTrial))
        }

        // update pixel
        var pixelObj = edit.createUpdateObject(edit.PIXEL_FIELDS, this.pixel)
        promiseList.push(account.updatePixel(this.selected.id, pixelObj))

        // update watchDOM
        for (let cid in this.campaigns) {
          const wd = this.campaigns[cid].watchDOM ? true : null
          promiseList.push(campaign.update(this.selected.id, this.campaigns[cid].campaignId, {watchDOM: wd}))
        }

        // update account index
        if (this.selected.customer_id) {
          promiseList.push(database.set(['account_index', this.selected.customer_id], this.selected.id))
        }

        // update onboarding state
        promiseList.push(database.update(['onboarding', this.selected.id], {state: this.onboardingState}))

        // add all promos to the user's account
        for (let promo in this.addedPromos) {
          promiseList.push(account.addPromo(this.selected.id, this.addedPromos[promo]))
        }

        q.all(promiseList)
          .then(() => {
            this.$toasted.show('Changes were Saved', {
              duration: 3000,
              className: 'toasty'
            })
          }).catch(err => {
            console.log('Save error: ', err)
            this.$toasted.show('Error: Some settings did not save', {
              duration: 3000,
              className: 'toasty-error'
            })
          }).finally(() => {
            this.saving = false
          })
      } else {
        this.$toasted.show('Nothing to save. Select an email first', {
          duration: 3000,
          className: 'toasty-error'
        })
      }
    },
    loadAccount (accountId) {
      this.loading = true
      // get account
      database
        .getSingle(['accounts', accountId])
        .then(async accountData => {
          if (accountData) {
            this.selected = accountData
            this.initialPlan = accountData.planId
            this.pixel = await account.getPixel(accountId)
            this.campaigns = await campaign.list(accountId)
            // get onboarding
            database
              .getSingle(['onboarding', accountId])
              .then(obData => {
                if (obData) {
                  this.onboardingState = obData.state
                }
              })
          } else {
            return Promise.reject(new Error('Account does not exist'))
          }
        }).catch(err => {
          console.log('Load error: ', err)
          this.$toasted.show('Unable to load account', {
            duration: 3000,
            className: 'toasty-error'
          })
        }).finally(() => {
          this.loading = false
        })
    },
    addPromo () {
      if (this.newPromo) {
        // New promos added
        this.addedPromos.push(this.newPromo)
        // Update list of promos for this account
        if (!this.selected.promos) {
          var promos = {}
          promos[this.newPromo] = true
          Vue.set(this.selected, 'promos', promos)
        } else {
          Vue.set(this.selected.promos, this.newPromo, true)
        }
      }
    },
    resetTraffic () {
      if (confirm("Reset this user's traffic?")) {
        this.saving = true
        // reset user's traffic
        database.set(['account_stats', this.selected.id], null)
          .then(() => {
            this.$toasted.show(`User's traffic was reset`, {
              duration: 3000,
              className: 'toasty'
            })
            this.saving = false
          }).catch(() => {
            this.saving = false
          })
      }
    },
    updateModifiers () {
      // remove modifier values if type is not set
      if (!this.selected.visitorLimitModifierType) {
        this.selected.visitorLimitModifierType = null
        this.selected.visitorLimitModifier = null
        this.selected.resetOnPlanChange = null
      }
    },
    togglePause () {
      if (confirm(`${this.pauseAction}?`)) {
        this.saving = true
        var pauseFunction = this.pixel.paused ? billing.unpause : billing.pause
        pauseFunction(this.selected.id)
          .then(() => {
            this.pixel.paused = !this.pixel.paused
            this.$toasted.show(`${this.pauseAction} successful`, {
              duration: 3000,
              className: 'toasty'
            })
            this.saving = false
          }).catch((err) => {
            this.$toasted.show(`${this.pauseAction} unsuccessful: ${err}`, {
              duration: 3000,
              className: 'toasty-error'
            })
            this.saving = false
          })
      }
    }
  },
  computed: {
    planChanged () {
      // check if the plan has been changed
      return this.initialPlan !== this.selected.planId
    },
    planUpgrade () {
      // determine whether the plan change is an upgrade
      var oldPlan = _find(this.allPlans, {id: this.initialPlan})
      var newPlan = _find(this.allPlans, {id: this.selected.planId})
      if (oldPlan && newPlan) {
        return (newPlan.amount - oldPlan.amount) > 0
      }
    },
    pauseAction () {
      return this.pixel.paused ? 'Unpause account' : 'Pause account'
    },
    visitorLimitModifier: {
      get () {
        return this.selected.visitorLimitModifier
      },
      set (val) {
        this.selected.visitorLimitModifier = Number.parseFloat(val)
      }
    }
  },
  watch: {
    $route (to, from) {
      // load account when selected from list
      if (to.params.id) {
        this.loadAccount(to.params.id)
      }
    }
  },
  mounted () {
    // get the list of available promo codes
    database
      .getSingle(['promos'])
      .then(list => {
        this.allPromos = Object.keys(list)
      })
    // get the list of available plans
    billing
      .listPlans()
      .then(plans => {
        // planID from account should never be 'paused'
        _remove(plans, {id: 'paused'})
        this.allPlans = plans
      })
    // load account if visiting URL directly
    if (this.$route.params.id) {
      this.loadAccount(this.$route.params.id)
    }
  }
}
</script>

<style lang="scss" module>
.section {
  margin-bottom: 1.1em;
}
.note {
  font-size: 11px;
}
.warn {
  color: red;
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/Users/edit/index.vue